/* eslint-disable */

import React, { ReactElement, useEffect, useState } from 'react';
import { Card, CardBody, Tabs, Tab, Text } from '@patternfly/react-core';
import {
	faTable,
	faDisplayChartUpCircleCurrency,
	faNewspaper,
	faPresentation,
	faTableList,
	faChartSimpleHorizontal,
} from '@fortawesome/pro-regular-svg-icons';
import { CardProps } from '../types/cards/card-props';
import ListCards from '../components/card/ListCards';
import { Favorites, TFavorites } from '../api/favorites/Favorites';
import { Recent } from '../api/recents/recents';
import { CardTypesEnum } from '../enums/card-types.enum';
import { RecentTypesEnum } from '../enums/recent-types.enum';
import { FavoriteTypes } from '../enums/favorite-types';
import { useIsGranted, useUser } from '../components/user/ApplicationProvider';
import { Permission } from '../enums/permission.enum';
import {
	sortByFavoriteThenRecentThenTimestamp,
	sortByTimestamp,
} from '../helpers/helper-components/recents-factory-helper';
import AlertCardsHomePage from '../helpers/helper-components/alert-cards-home-page';
import PageTitleSubheader from '../layout/subheader/PageTitleSubheader';
import { useOutletContext } from 'react-router-dom';
import MainSubNavContext from '../helpers/sub-nav-contexts/main-sub-nav-context';
import { SubNavContext } from '../helpers/sub-nav-contexts/sub-nav-context';
import { OutletContext } from '../layout/Layout';
import { Present } from '../api/present/Present';
import { Report } from '../api/reports/Reports';
import { Dataframe } from '../api/dataframes/Dataframes';
import { Chart } from '../api/charts/Chart';
import { Dashboard } from '../api/dashbboards/Dashboards';
import Loader from '../components/util/Loader';
import { ChartIconMap } from '../helpers/helper-components/chart-helper';
import { ChartTypesEnum } from '../enums/chart-types.enum';
import { useMount } from 'react-use';
import DiscoverSearch from './discover/DiscoverSearch';
import { TNewDateRange } from '../api/types/TNewDateRange';
import { TQuestion } from '../api/questions/Questions';
import { DiscoverCategory } from '../types/discover';
import { QuestionCategory } from '../api/question-category/QuestionCategory';
import { iconMap } from '../helpers/global-icon-map.helper';
import aiAnalystLogo from '../img/ai-analyst-placeholder.png';

function GettingStarted(): ReactElement {
	const { setSubSide, subNavExpanded, setSubNavExpanded } = useOutletContext<OutletContext>();
	const [filteredItems, setFilteredItems] = useState<CardProps[]>([]);
	const [combinedItems, setCombinedItems] = useState<CardProps[]>([]);
	const [recentItems, setRecentItems] = useState<CardProps[]>([]);
	const [favoriteItems, setFavoriteItems] = useState<CardProps[]>([]);
	const [activeTabKey, setActiveTabKey] = useState<string>('all');
	const currentUser = useUser();
	const isGranted = useIsGranted();
	const hasViewRecentPerm = isGranted(Permission.ViewRecent);
	const hasViewFavoritePerm = isGranted(Permission.ViewFavorite);
	const [subNavContext, setSubNavContext] = useState<SubNavContext>(MainSubNavContext);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const [searchTerm, setSearchTerm] = useState<string>('');
	const [categories, setCategories] = useState<DiscoverCategory[]>();
	const [hasLoadedCategories, setHasLoadedCategories] = useState<boolean>(false);
	const [selectedDate, setSelectedDate] = useState<TNewDateRange>();
	const [allQuestions, setAllQuestions] = useState<TQuestion[]>();
	const [aiSearchEnabled, setAiSearchEnabled] = useState(true);
	const isOnSearchRoute = window.location.pathname === '/discover/search';
	const [inlineCategoryId, setInlineCategoryId] = useState<number | undefined>(undefined);

	// Open a category inline; if the same category is clicked again, force a replace
	const openCategory = (id: number) => {
		if (inlineCategoryId === id) {
			// unset then set to force re-render/reload
			setInlineCategoryId(undefined);
			setTimeout(() => setInlineCategoryId(id), 0);
		} else {
			setInlineCategoryId(id);
		}
	};

	useMount(() => {
		Favorites.GetByType(FavoriteTypes.chart).then((response) => {
			const pinnedChart = response.find((x) => x.showOnMain);

			if (pinnedChart) {
				setSubNavContext({
					...subNavContext,
					reportSnapshot: {
						snapshotText: 'From your Favorites',
						title: pinnedChart.name,
						chartId: pinnedChart.entity_id,
					},
				});
			} else {
				setSubNavContext({
					...subNavContext,
					reportSnapshot: {
						snapshotText: '',
						title: '',
						chartId: undefined,
					},
				});
			}
		});

		const categoryQuestions: TQuestion[] = [];

		void QuestionCategory.GetPermitted(['questions']).then((response) => {
			const discoverCategories: DiscoverCategory[] = [];

			response.map((item) => {
				const newCategory: DiscoverCategory = {
					icon: iconMap[item.icon],
					id: item.id!,
					key: `Category_${item.title}`,
					name: item.title,
					sequence: item.sequence ?? 0,
					questions: [],
				};
				discoverCategories.push(newCategory);

				const questions: TQuestion[] = (
					(item.questions as TQuestion[]) ?? ([] as TQuestion[])
				).map((q: TQuestion) => ({
					chart: q.chart,
					description: q.description,
					name: q.name,
					sequence: q.sequence,
					category: newCategory.id,
				}));

				questions.map((q) => {
					categoryQuestions.push(q);
				});
			});

			setCategories(
				discoverCategories.sort((a, b) => {
					const seqDiff = (a.sequence ?? 0) - (b.sequence ?? 0);
					if (seqDiff !== 0) return seqDiff;
					return a.name.localeCompare(b.name);
				})
			);
			setAllQuestions(categoryQuestions);
			setHasLoadedCategories(true);
		});
	});

	useEffect(() => {
		setSubSide({
			subheaderContext: (
				<PageTitleSubheader
					pageTitle=""
					pageDescription=""
					expanded={subNavExpanded}
					setExpanded={setSubNavExpanded}
					isCollapsable
					subNavContext={subNavContext}
				/>
			),
			sidebarContext: null,
			additionalClassNames: 'home-page-subbar no-title',
			hideLeftSideBar: true,
		});
	}, [setSubSide, subNavExpanded, setSubNavExpanded, subNavContext]);

	useEffect(() => {
		setIsLoading(true);
		if (hasViewRecentPerm) {
			fetchRecentItems();
		}
		if (hasViewFavoritePerm) {
			fetchFavoriteItems();
		}
	}, [hasViewRecentPerm, hasViewFavoritePerm]);

	// Fetch recent items
	const fetchRecentItems = () => {
		const promises = [
			Recent.GetAll(RecentTypesEnum.Dataframe),
			Recent.GetAll(RecentTypesEnum.Charts),
			Recent.GetAll(RecentTypesEnum.Dashboard),
			Recent.GetAll(RecentTypesEnum.Report),
			Recent.GetAll(RecentTypesEnum.Presentation),
			Recent.GetAll(RecentTypesEnum.Report, true),
		];

		Promise.all(promises)
			.then(([dataframes, charts, dashboards, reports, presentations, tables]) => {
				const recentDataframeCards = dataframes.map((item) => ({
					icon: faTable,
					type: CardTypesEnum.DataFrame,
					url: `/analyze/${CardTypesEnum.DataFrame}/view/${item.entity_id}`,
					title: item.name ?? '',
					timestamp: item.accessed_at,
					id: item.entity_id,
					isRecent: true,
				}));

				const recentChartCards = charts.map((item) => ({
					icon: item.chart_type
						? ChartIconMap[item.chart_type as ChartTypesEnum]
						: faChartSimpleHorizontal,
					type: CardTypesEnum.Chart,
					url: `/analyze/${CardTypesEnum.Chart}/view/chart/${item.entity_id}`,
					title: item.name ?? '',
					timestamp: item.accessed_at,
					id: item.entity_id,
					isRecent: true,
				}));

				const recentDashboardCards = dashboards.map((item) => ({
					icon: faDisplayChartUpCircleCurrency,
					type: CardTypesEnum.Dashboard,
					url: `/analyze/${CardTypesEnum.Dashboard}/view/${item.entity_id}`,
					title: item.name ?? '',
					timestamp: item.accessed_at,
					id: item.entity_id,
					isRecent: true,
				}));

				const recentReportCards = reports.map((item) => ({
					icon: faNewspaper,
					type: CardTypesEnum.Report,
					url: `/report/view/${item.entity_id}`,
					title: item.name ?? '',
					timestamp: item.accessed_at,
					id: item.entity_id,
					isRecent: true,
				}));

				const recentPresentationCards = presentations.map((item) => ({
					icon: faPresentation,
					type: CardTypesEnum.Presentation,
					url: `/present/view/${item.entity_id}`,
					title: item.name ?? '',
					timestamp: item.accessed_at,
					id: item.entity_id,
					isRecent: true,
				}));

				const recentTablesCards = tables
					.filter((x) => x.entity_type === RecentTypesEnum.Report)
					.map((item) => ({
						icon: faTableList,
						type: CardTypesEnum.Table,
						url: `/table/view/${item.entity_id}`,
						title: item.name ?? '',
						timestamp: item.accessed_at,
						id: item.entity_id,
						isRecent: true,
					}));

				const allRecentItems = [
					...recentDataframeCards,
					...recentChartCards,
					...recentDashboardCards,
					...recentReportCards,
					...recentPresentationCards,
					...recentTablesCards,
				];

				setIsLoading(false);
				setRecentItems(sortByTimestamp(allRecentItems));
			})
			.catch((error) => console.error('Error fetching recent data:', error));
	};

	// Fetch favorite items
	const fetchFavoriteItems = () => {
		const favoritePromises = [
			Favorites.GetByType(FavoriteTypes.dataframe),
			Favorites.GetByType(FavoriteTypes.chart),
			Favorites.GetByType(FavoriteTypes.report),
			Favorites.GetByType(FavoriteTypes.dashboard),
			Favorites.GetByType(FavoriteTypes.table),
			Favorites.GetByType(FavoriteTypes.presentation),
		];

		Promise.all(favoritePromises)
			.then(([dataframes, charts, reports, dashboards, tables, presentations]) => {
				const favoriteDataframeCards = dataframes.map((item: TFavorites) => ({
					icon: faTable,
					type: CardTypesEnum.DataFrame,
					url: `/analyze/${CardTypesEnum.DataFrame}/view/${item.entity_id}`,
					title: item.name ?? '',
					id: item.entity_id,
					isFavorite: true,
					showOnMain: item.showOnMain,
				}));

				const favoriteChartCards = charts.map((item: TFavorites) => ({
					icon: item.chartType
						? ChartIconMap[item.chartType as ChartTypesEnum]
						: faChartSimpleHorizontal,
					type: CardTypesEnum.Chart,
					url: `/analyze/${CardTypesEnum.Chart}/view/chart/${item.entity_id}`,
					title: item.name ?? '',
					id: item.entity_id,
					isFavorite: true,
					showOnMain: item.showOnMain,
				}));

				const favoriteDashboardCards = dashboards.map((item: TFavorites) => ({
					icon: faDisplayChartUpCircleCurrency,
					type: CardTypesEnum.Dashboard,
					url: `/analyze/${CardTypesEnum.Dashboard}/view/${item.entity_id}`,
					title: item.name ?? '',
					id: item.entity_id,
					isFavorite: true,
					showOnMain: item.showOnMain,
				}));

				const favoriteReportCards = reports.map((item: TFavorites) => ({
					icon: faNewspaper,
					type: CardTypesEnum.Report,
					url: `/report/view/${item.entity_id}`,
					title: item.name ?? '',
					id: item.entity_id,
					isFavorite: true,
					showOnMain: item.showOnMain,
				}));

				const favoriteTableCards = tables.map((item: TFavorites) => ({
					icon: faTableList,
					type: CardTypesEnum.Table,
					url: `/table/view/${item.entity_id}`,
					title: item.name ?? '',
					id: item.entity_id,
					isFavorite: true,
					showOnMain: item.showOnMain,
				}));

				const favoritePresentationCards = presentations.map((item: TFavorites) => ({
					icon: faPresentation,
					type: CardTypesEnum.Presentation,
					url: `/present/view/${item.entity_id}`,
					title: item.name ?? '',
					id: item.entity_id,
					isFavorite: true,
					showOnMain: item.showOnMain,
				}));

				setIsLoading(false);
				setFavoriteItems([
					...favoriteDataframeCards,
					...favoriteChartCards,
					...favoriteDashboardCards,
					...favoriteReportCards,
					...favoriteTableCards,
					...favoritePresentationCards,
				]);
			})
			.catch((error) => console.error('Error fetching favorite data:', error));
	};

	useEffect(() => {
		if (favoriteItems.length > 0 || recentItems.length > 0) {
			const combined = [
				// All favorites first
				...favoriteItems.map((item) => ({
					...item,
					isFavorite: true,
					isRecent: false,
					tooltip: 'Favorite',
				})),

				// Then only recents that aren't in favorites
				...recentItems
					.filter((recent) => !favoriteItems.some((fav) => fav.id === recent.id))
					.map((item) => ({
						...item,
						isFavorite: false,
						isRecent: true,
						tooltip: 'Recently Viewed',
					})),
			];

			setFilteredItems(sortByFavoriteThenRecentThenTimestamp(combined.slice(0, 18)));
			setCombinedItems(sortByFavoriteThenRecentThenTimestamp(combined));
		}
	}, [favoriteItems, recentItems]);

	const populateData = async (tabKey: string) => {
		// Step 1: Filter current combined items
		let filtered =
			tabKey === 'all' ? combinedItems : combinedItems.filter((item) => item.type === tabKey);

		// Step 2: Only fetch if we need more
		if (filtered.length < 18) {
			try {
				let data: any[] = [];
				let fetchedItems: any[] = [];

				switch (tabKey) {
					case CardTypesEnum.Dashboard:
						data = await Dashboard.GetAll();
						fetchedItems = data.map((item) => ({
							icon: faDisplayChartUpCircleCurrency,
							type: CardTypesEnum.Dashboard,
							url: `/analyze/${CardTypesEnum.Dashboard}/view/${item.id}`,
							title: item.name ?? '',
							timestamp: item.accessed_at,
							id: item.id,
						}));
						break;

					case CardTypesEnum.Chart:
						data = await Chart.GetAll();
						fetchedItems = data.map((item) => ({
							icon: item.chart_type
								? ChartIconMap[item.chart_type as ChartTypesEnum]
								: faChartSimpleHorizontal,
							type: CardTypesEnum.Chart,
							url: `/analyze/${CardTypesEnum.Chart}/view/chart/${item.id}`,
							title: item.name ?? '',
							timestamp: item.accessed_at,
							id: item.id,
						}));
						break;

					case CardTypesEnum.Table:
						data = await Report.GetAll([], true);
						fetchedItems = data
							.filter((x) => x.entity_type === RecentTypesEnum.Report)
							.map((item) => ({
								icon: faTableList,
								type: CardTypesEnum.Table,
								url: `/table/view/${item.id}`,
								title: item.name ?? '',
								timestamp: item.accessed_at,
								id: item.id,
							}));
						break;

					case CardTypesEnum.DataFrame:
						data = await Dataframe.GetAll();
						fetchedItems = data.map((item) => ({
							icon: faTable,
							type: CardTypesEnum.DataFrame,
							url: `/analyze/${CardTypesEnum.DataFrame}/view/${item.id}`,
							title: item.name ?? '',
							timestamp: item.accessed_at,
							id: item.id,
						}));
						break;

					case CardTypesEnum.Report:
						data = await Report.GetAll([], false);
						fetchedItems = data.map((item) => ({
							icon: faNewspaper,
							type: CardTypesEnum.Report,
							url: `/report/view/${item.id}`,
							title: item.name ?? '',
							timestamp: item.accessed_at,
							id: item.id,
						}));
						break;

					case CardTypesEnum.Presentation:
						data = await Present.GetAll();
						fetchedItems = data.map((item) => ({
							icon: faPresentation,
							type: CardTypesEnum.Presentation,
							url: `/present/view/${item.id}`,
							title: item.name ?? '',
							timestamp: item.accessed_at,
							id: item.id,
						}));
						break;
				}

				// Step 3: Combine — avoid duplicates by ID, and cap at 18
				const uniqueExistingIds = new Set(filtered.map((f) => f.id));
				const newUniqueItems = fetchedItems.filter((f) => !uniqueExistingIds.has(f.id));

				const totalNeeded = 18 - filtered.length;
				const itemsToAdd = newUniqueItems.slice(0, totalNeeded);

				filtered = [...filtered, ...itemsToAdd].slice(0, 18);
			} catch (error) {
				console.error(`Error fetching data for tab ${tabKey}:`, error);
			}
		} else if (tabKey == 'all') {
			filtered = combinedItems.slice(0, 18);
		}

		return filtered;
	};

	const handleTabSelect = async (_: any, tabKey: string | number) => {
		setActiveTabKey(tabKey as string);
		setIsLoading(true);
		const filtered = await populateData(tabKey.toString());
		setIsLoading(false);
		// Step 4: Update the state
		setFilteredItems(filtered.slice(0, 18));
	};

	return (
		<>
			{aiSearchEnabled && (
				<>
					<Card>
						<CardBody style={{ position: 'relative' }}>
							<div>
								<img
									src={aiAnalystLogo}
									style={{
										position: 'absolute',
										top: 0,
										right: 0,
										width: '30%',
										height: '100%',
										objectFit: 'cover',
										opacity: 0.7,
										zIndex: 0,
										maxHeight: 'unset',
									}}
								/>
							</div>

							<div style={{ position: 'relative', zIndex: 1 }}>
								<DiscoverSearch
									searchTerm={searchTerm}
									setSearchTerm={setSearchTerm}
									aiSearchEnabled={aiSearchEnabled}
									setAiSearchEnabled={setAiSearchEnabled}
									categories={categories}
									allQuestions={allQuestions}
									hasLoadedCategories={hasLoadedCategories}
									openCategory={openCategory}
									setSelectedDate={setSelectedDate}
									isOnSearchRoute={isOnSearchRoute}
								/>
							</div>
						</CardBody>
					</Card>
					<br />
				</>
			)}
			<div>
				<Card className="combined-items-card-panel">
					<CardBody>
						{isLoading && <Loader />}
						{!isLoading && (
							<Tabs
								activeKey={activeTabKey}
								onSelect={(event, key) => {
									setActiveTabKey(key as string);
									handleTabSelect(event, key);
								}}
								className="parent-tabs"
							>
								<Tab
									eventKey="all"
									title="All"
								>
									<ListCards
										hideEllipsis
										items={filteredItems}
									/>
								</Tab>
								<Tab
									eventKey={CardTypesEnum.Dashboard.toLowerCase()}
									title="Dashboards"
								>
									<ListCards
										hideEllipsis
										items={filteredItems}
									/>
								</Tab>
								<Tab
									eventKey={CardTypesEnum.Chart.toLowerCase()}
									title="Charts"
								>
									<ListCards
										hideEllipsis
										items={filteredItems}
									/>
								</Tab>
								<Tab
									eventKey={CardTypesEnum.Table.toLowerCase()}
									title="Tables"
								>
									<ListCards
										hideEllipsis
										items={filteredItems}
									/>
								</Tab>
								<Tab
									eventKey={CardTypesEnum.DataFrame.toLowerCase()}
									title="Dataframes"
								>
									<ListCards
										hideEllipsis
										items={filteredItems}
									/>
								</Tab>
								<Tab
									eventKey={CardTypesEnum.Report.toLowerCase()}
									title="Reports"
								>
									<ListCards
										hideEllipsis
										items={filteredItems}
									/>
								</Tab>
								<Tab
									eventKey={CardTypesEnum.Presentation.toLowerCase()}
									title="Presentations"
								>
									<ListCards
										hideEllipsis
										items={filteredItems}
									/>
								</Tab>
							</Tabs>
						)}
					</CardBody>
				</Card>

				{/* Alerts Section */}
				<br />
				<Card>
					<CardBody>
						<Text className="font-size-md">
							<strong>Alerts</strong>
						</Text>
						<br />
						<AlertCardsHomePage />
					</CardBody>
				</Card>
			</div>
		</>
	);
}

export default GettingStarted;
